import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";

import { clearToken, currentClaims } from "../lib/auth";
import { useEnterpriseBranding } from "../lib/useEnterpriseBranding";
import { commodityLabel, useDHDistrict, useDHGodowns, useMe } from "./data";
import "../statehead/statehead.css";

/** The District Head experience shares the State Head's chrome — the
 * dual-brand top bar and navy rail — but scoped one level down: their
 * rail lists Godowns instead of Districts, and the scope chip shows
 * their district. */
export function DistrictHeadShell() {
  const claims = currentClaims();
  const navigate = useNavigate();
  const { enterprise } = useEnterpriseBranding();
  const meQ = useMe();
  const districtQ = useDHDistrict();
  const godownsQ = useDHGodowns();
  const [menuOpen, setMenuOpen] = useState(false);

  const district = districtQ.data?.district;
  const godownCount = godownsQ.data?.godowns.length;
  const user = meQ.data?.user;
  const email = user?.email || claims?.email || "";
  const fullName = user
    ? `${user.first_name} ${user.last_name}`.trim()
    : email;
  const initials =
    (user
      ? (user.first_name[0] || "") + (user.last_name[0] || "")
      : email.slice(0, 2)
    ).toUpperCase() || "DH";
  const commodity = commodityLabel(godownsQ.data?.godowns ?? []);

  const signOut = () => {
    clearToken();
    navigate("/login", { replace: true });
  };

  return (
    <div className="sh">
      <header className="topbar">
        <img className="logo" src="/logo-app.png" alt="AgriLens.AI" />
        <div className="tie" aria-hidden="true" />
        <div className="client">
          {enterprise?.logo_url && (
            <img
              className="client-logo"
              src={enterprise.logo_url}
              alt={enterprise.name}
            />
          )}
          <div className="client-name">
            {enterprise?.name ?? "—"}
            <em>
              CLIENT · {enterprise?.slug ? enterprise.slug.toUpperCase() : "—"}
            </em>
          </div>
        </div>
        <div className="topbar-right">
          {commodity && <span className="env">{commodity}</span>}
          <span className="env">DISTRICT HEAD ACCESS</span>
          <div className="profile-wrap">
            <button
              className="chip"
              aria-haspopup="true"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="avatar">{initials}</span>
              <span className="chip-text">
                <b>{fullName || "—"}</b>
                <i>{district ? district.name.toUpperCase() : "—"}</i>
              </span>
              <svg className="caret" viewBox="0 0 12 12" fill="none">
                <path
                  d="M3 5l3 3 3-3"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <div className={"menu" + (menuOpen ? " open" : "")} role="menu">
              <div className="menu-head">
                <b>{fullName || "—"}</b>
                <span>
                  District Head{district ? ` — ${district.name}` : ""}
                </span>
                <span className="mono">{email || "—"}</span>
              </div>
              <Link to="/dh/profile" role="menuitem">
                <button onClick={() => setMenuOpen(false)}>View profile</button>
              </Link>
              <hr />
              <button className="danger" onClick={signOut}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="shell">
        <nav className="rail">
          <p className="rail-label">DISTRICT</p>
          <NavLink
            to="/dh"
            end
            className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
          >
            <svg viewBox="0 0 16 16" fill="none">
              <rect x="1.5" y="1.5" width="5.5" height="5.5" stroke="currentColor" strokeWidth="1.4" />
              <rect x="9" y="1.5" width="5.5" height="5.5" stroke="currentColor" strokeWidth="1.4" />
              <rect x="1.5" y="9" width="5.5" height="5.5" stroke="currentColor" strokeWidth="1.4" />
              <rect x="9" y="9" width="5.5" height="5.5" stroke="currentColor" strokeWidth="1.4" />
            </svg>
            Overview
          </NavLink>
          <NavLink
            to="/dh/godowns"
            className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
          >
            <svg viewBox="0 0 16 16" fill="none">
              <path d="M2 6.5L8 2l6 4.5V14H2V6.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
              <path d="M6.5 14V9h3v5" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
            </svg>
            Godowns
            {godownCount != null && (
              <span className="count">{godownCount}</span>
            )}
          </NavLink>
          <NavLink
            to="/dh/reports"
            className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
          >
            <svg viewBox="0 0 16 16" fill="none">
              <path d="M3.5 1.5h6L13 5v9.5H3.5v-13z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
              <path d="M6 8h4M6 11h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            Reports
          </NavLink>
          <div className="rail-foot">
            AgriLens Portal
            <br />
            District: {district ? district.name : "—"}
          </div>
        </nav>

        <main className="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
